/* Het schuifbeeld op de pagina van een energieoplossing (ADR 0024): twee beelden van dezelfde plek
 * over elkaar, met een schuif van "voor" naar "na". In de database is het de JSON-kolom `before_after`
 * op website.solutions: één object
 *   { "title", "text", "credit", "before": { "image", "caption", "alt" }, "after": { … } }
 * Alleen `image` van beide beelden is verplicht (lib/schuifbeeld.ts en
 * db/voorstel/2026-10-09-schuifbeeld.sql in de site-repo).
 *
 * De site toont het schuifbeeld alleen als beide beelden er zijn en uit de eigen media komen. Bij al
 * het andere toont ze niets, ook de kop en de tekst niet, en zonder melding. Daarom slaat de CRM alleen
 * op wat de site toont, of een leeg schuifbeeld: een half schuifbeeld zou stil van de pagina wegblijven.
 *
 * Dezelfde bescherming als bij "In het kort" (./kerngegevens):
 *   - Het object in het concept is de enige waarheid. Een wijziging verandert alleen het aangeraakte vak.
 *   - Wat er geschreven wordt, is het object uit de database met daarin alleen de gewijzigde vakken
 *     vervangen. Sleutels die de bewerker niet kent, blijven dus staan.
 *   - Is geen vak anders, dan wordt de kolom niet geschreven.
 *   - Herkent de bewerker de vorm in de database niet, dan blijft de kaart alleen-lezen.
 *   - Een vak dat leeg is gemaakt, verdwijnt als sleutel uit het object.
 *
 * De kolom komt uit een script dat de eigenaar van de database zelf draait. Heeft een rij de sleutel
 * `before_after` niet, dan wordt hij nooit geschreven (zie ./website). */

import { VOORVOEGSEL, keurBeeldpad } from './beeldpad';

export const KOLOM = 'before_after';
/** Het script in de site-repo (greenvision-site) dat de kolom toevoegt. */
export const SCRIPT = 'db/voorstel/2026-10-09-schuifbeeld.sql';
export const LABEL = 'Schuifbeeld';

export type Kant = 'before' | 'after';
export type Tekstveld = 'title' | 'text' | 'credit';
export type Beeldveld = 'image' | 'caption' | 'alt';

export const KANTEN: ReadonlyArray<{ kant: Kant; naam: string; standaard: string; plek: string }> = [
  // `standaard` is het bijschrift dat de site zelf zet als het ontbreekt.
  { kant: 'before', naam: 'Beeld voor', standaard: 'Voor', plek: 'links van de schuif' },
  { kant: 'after', naam: 'Beeld na', standaard: 'Na', plek: 'rechts van de schuif' },
];
const TEKSTVELDEN: Tekstveld[] = ['title', 'text', 'credit'];
const BEELDVELDEN: Beeldveld[] = ['image', 'caption', 'alt'];

type Voorwerp = Record<string, unknown>;
const isVoorwerp = (w: unknown): w is Voorwerp => typeof w === 'object' && w !== null && !Array.isArray(w);
const isTekstOfLeeg = (w: unknown): boolean => w == null || typeof w === 'string';
const tekst = (w: unknown): string => (typeof w === 'string' ? w : '');
const soortNaam = (w: unknown): string => (typeof w === 'string' ? 'een losse tekst' : Array.isArray(w) ? 'een rij' : typeof w === 'number' ? 'een getal' : 'een losse waarde');

/** Heeft deze rij de kolom? Een rij uit een database zonder het script heeft de sleutel niet. */
export const heeftSchuifbeeld = (rij: Record<string, unknown> | null | undefined): boolean => !!rij && KOLOM in rij;

/* ── Wat de site doet ──────────────────────────────────────────────────────── */

/* De regel van de site, één op één (schuifbeeld() in lib/schuifbeeld.ts, met ownMedia uit
 * lib/media.ts): alleen echte tekst telt, zonder spaties aan de randen, en een beeld komt uit de eigen
 * media. De site neemt daarnaast een heel adres van haar eigen opslag aan (MEDIA_ORIGIN, uit haar
 * omgeving). Dat kent de CRM niet: hier telt alleen een pad dat met /media/ begint. */
const echteTekst = (w: unknown): string | undefined => (typeof w === 'string' && w.trim() ? w.trim() : undefined);
const siteBeeld = (w: unknown): boolean => isVoorwerp(w) && !!echteTekst(w.image)?.startsWith(VOORVOEGSEL);

/** Toont de site met deze waarde een schuifbeeld? */
export const staatOpSite = (waarde: unknown): boolean => isVoorwerp(waarde) && siteBeeld(waarde.before) && siteBeeld(waarde.after);

/* ── Lezen ─────────────────────────────────────────────────────────────────── */

export interface Beeld { beeld: string; bijschrift: string; beschrijving: string }

/** `reden` staat er alleen als de vorm onbekend is (ok is dan false). */
export interface Lezing { ok: boolean; reden?: string; kop: string; tekst: string; bron: string; voor: Beeld; na: Beeld }

const GEEN_BEELD = (): Beeld => ({ beeld: '', bijschrift: '', beschrijving: '' });
const LEEG = (): Lezing => ({ ok: true, kop: '', tekst: '', bron: '', voor: GEEN_BEELD(), na: GEEN_BEELD() });
const onbekend = (reden: string): Lezing => ({ ...LEEG(), ok: false, reden });

/** Herkent de bewerker deze vorm? Leeg (null of {}) telt als geen schuifbeeld. */
export function leesSchuifbeeld(waarde: unknown): Lezing {
  if (waarde == null) return LEEG();
  if (!isVoorwerp(waarde)) return onbekend(`Er staat geen schuifbeeld maar ${soortNaam(waarde)}.`);
  for (const veld of TEKSTVELDEN) if (!isTekstOfLeeg(waarde[veld])) return onbekend(`Bij "${veld}" staat iets anders dan tekst.`);
  const beelden = {} as Record<Kant, Beeld>;
  for (const { kant } of KANTEN) {
    const b = waarde[kant];
    if (b != null && !isVoorwerp(b)) return onbekend(`Bij "${kant}" staat geen beeld met een pad, een bijschrift en een beschrijving.`);
    const o: Voorwerp = isVoorwerp(b) ? b : {};
    for (const veld of BEELDVELDEN) if (!isTekstOfLeeg(o[veld])) return onbekend(`Bij "${kant}.${veld}" staat iets anders dan tekst.`);
    beelden[kant] = { beeld: tekst(o.image), bijschrift: tekst(o.caption), beschrijving: tekst(o.alt) };
  }
  return { ok: true, kop: tekst(waarde.title), tekst: tekst(waarde.text), bron: tekst(waarde.credit), voor: beelden.before, na: beelden.after };
}

/* ── Eén vak wijzigen ──────────────────────────────────────────────────────── */

/** De kop, de tekst of de bronvermelding wijzigen. De rest van het object blijft letterlijk staan; een
 *  leeg vak haalt de sleutel weg. */
export function zetTekst(waarde: unknown, veld: Tekstveld, invoer: string): Voorwerp {
  const uit: Voorwerp = isVoorwerp(waarde) ? { ...waarde } : {};
  if (invoer === '') delete uit[veld];
  else uit[veld] = invoer;
  return uit;
}

/** Eén vak van één beeld wijzigen. Een beeld waar niets meer in staat, verdwijnt als sleutel. */
export function zetBeeld(waarde: unknown, kant: Kant, veld: Beeldveld, invoer: string): Voorwerp {
  const uit: Voorwerp = isVoorwerp(waarde) ? { ...waarde } : {};
  const beeld: Voorwerp = isVoorwerp(uit[kant]) ? { ...(uit[kant] as Voorwerp) } : {};
  if (invoer === '') delete beeld[veld];
  else beeld[veld] = invoer;
  if (Object.keys(beeld).length === 0) delete uit[kant];
  else uit[kant] = beeld;
  return uit;
}

/* ── Vergelijken ───────────────────────────────────────────────────────────── */

type Pad = [Tekstveld] | [Kant, Beeldveld];
const PADEN: Pad[] = [...TEKSTVELDEN.map((veld): Pad => [veld]), ...KANTEN.flatMap(({ kant }) => BEELDVELDEN.map((veld): Pad => [kant, veld]))];

/** De tekst op één plek, zoals hij geschreven wordt: zonder spaties aan de randen. */
function op(waarde: unknown, pad: Pad): string {
  if (!isVoorwerp(waarde)) return '';
  if (pad.length === 1) return tekst(waarde[pad[0]]).trim();
  const beeld = waarde[pad[0]];
  return isVoorwerp(beeld) ? tekst(beeld[pad[1]]).trim() : '';
}

/** Welke vakken zijn echt anders dan in de database? Alleen een spatie erbij is niets. */
const gewijzigd = (opgeslagen: unknown, concept: unknown): Pad[] => PADEN.filter((pad) => op(opgeslagen, pad) !== op(concept, pad));

/** Staat er in geen enkel vak iets? Dan is er geen schuifbeeld. Sleutels die de bewerker niet kent,
 *  tellen niet mee: de site toont er niets van. Iets anders dan een object is niet leeg maar onbekend. */
export const isLeeg = (waarde: unknown): boolean => (waarde == null || isVoorwerp(waarde)) && PADEN.every((pad) => op(waarde, pad) === '');

/** Het concept zonder schuifbeeld: alle vakken leeg. Wat de bewerker niet kent, blijft staan. */
export function maakLeeg(waarde: unknown): Voorwerp {
  const uit: Voorwerp = isVoorwerp(waarde) ? { ...waarde } : {};
  for (const veld of TEKSTVELDEN) delete uit[veld];
  for (const { kant } of KANTEN) {
    const beeld: Voorwerp = isVoorwerp(uit[kant]) ? { ...(uit[kant] as Voorwerp) } : {};
    for (const veld of BEELDVELDEN) delete beeld[veld];
    if (Object.keys(beeld).length === 0) delete uit[kant];
    else uit[kant] = beeld;
  }
  return uit;
}

/* De database (jsonb) bewaart de volgorde van sleutels niet: vergelijk dus op inhoud. */
function canoniek(w: unknown): string {
  if (Array.isArray(w)) return `[${w.map(canoniek).join(',')}]`;
  if (isVoorwerp(w)) return `{${Object.keys(w).filter((k) => w[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${canoniek(w[k])}`).join(',')}}`;
  return JSON.stringify(w) ?? 'null';
}

/* ── Nakijken en schrijven ─────────────────────────────────────────────────── */

export type Staat = 'leeg' | 'compleet' | 'niet_af';

/** Wat de site met deze waarde doet, voor de regel onder de vakken. */
export const staatVan = (waarde: unknown): Staat => (isLeeg(waarde) ? 'leeg' : watMist(waarde) === null ? 'compleet' : 'niet_af');

export const STAAT_ZIN: Record<Staat, string> = {
  leeg: 'Leeg: op de pagina van deze oplossing staat geen schuifbeeld.',
  compleet: 'Compleet: met deze twee beelden toont de site het schuifbeeld.',
  niet_af: 'Nog niet compleet: de site toont een schuifbeeld alleen met twee beelden uit de eigen media. Zo is het niet op te slaan.',
};

/** Waarom de site dit schuifbeeld niet zou tonen, of null. Voor een schuifbeeld waar iets in staat. */
function watMist(waarde: unknown): string | null {
  for (const { kant, naam } of KANTEN) {
    const pad = op(waarde, [kant, 'image']);
    if (pad === '') return `het ${naam.toLowerCase()} ontbreekt. De site toont een schuifbeeld alleen met twee beelden: kies het beeld, of maak het schuifbeeld leeg.`;
    const fout = keurBeeldpad(pad);
    if (fout) return `bij het ${naam.toLowerCase()} klopt het pad niet. ${fout}`;
  }
  return null;
}

/** Wat er mis is met het schuifbeeld, of null. Alleen een schuifbeeld dat is aangeraakt, wordt
 *  nagekeken: oude inhoud houdt het opslaan van een ander veld niet tegen. */
export function controleerSchuifbeeld(opgeslagen: unknown, concept: unknown): string | null {
  if (!leesSchuifbeeld(opgeslagen).ok) {
    return canoniek(opgeslagen) === canoniek(concept) ? null : `${LABEL}: dit schuifbeeld heeft een vorm die de bewerker niet kent. Het is hier niet te wijzigen.`;
  }
  const nieuw = leesSchuifbeeld(concept);
  if (!nieuw.ok) return `${LABEL}: ${nieuw.reden}`;
  if (gewijzigd(opgeslagen, concept).length === 0 || isLeeg(concept)) return null;
  const mist = watMist(concept);
  return mist ? `${LABEL}: ${mist}` : null;
}

/** Wat er geschreven moet worden, of null als er niets te schrijven is: niets gewijzigd, een onbekende
 *  vorm in de database of in het concept, of een schuifbeeld dat de site niet zou tonen. De basis is
 *  het object uit de database; alleen de gewijzigde vakken zijn vervangen, en een leeg vak is weg. */
export function teSchrijven(opgeslagen: unknown, concept: unknown): { waarde: Voorwerp } | null {
  if (!leesSchuifbeeld(opgeslagen).ok || !leesSchuifbeeld(concept).ok || controleerSchuifbeeld(opgeslagen, concept)) return null;
  const anders = gewijzigd(opgeslagen, concept);
  if (anders.length === 0) return null;
  let uit: Voorwerp = isVoorwerp(opgeslagen) ? opgeslagen : {};
  for (const pad of anders) {
    const nieuw = op(concept, pad);
    uit = pad.length === 1 ? zetTekst(uit, pad[0], nieuw) : zetBeeld(uit, pad[0], pad[1], nieuw);
  }
  return { waarde: uit };
}
